import React, { useState } from 'react';
import {
  Users,
  Target,
  TrendingUp,
  MessageSquare,
  ArrowRight,
  PieChart,
  FileText,
  Zap,
  ShieldCheck,
  CheckCircle2,
  Terminal,
  Activity,
  Workflow,
  BarChart3,
  Mail
} from 'lucide-react';
import { Button } from '../components/ui/button';

const CRMSolution = () => {
  const [activeModule, setActiveModule] = useState('sales');

  const crmPillars = [
    {
      icon: Target,
      title: 'Automated Sales Pipeline & Kanban',
      badge: 'Pipeline Velocity',
      color: 'from-orange-500 to-amber-500',
      description: 'Visual deal stages, automated lead assignment rules, stage movement triggers, and real-time deal probability scoring.',
      bullets: ['Visual Kanban & Table Views', 'Automated Lead Routing Rules', 'Deal Stagnation Warning Alerts']
    },
    {
      icon: Zap,
      title: 'AI Lead Scoring & Predictive Insights',
      badge: 'Machine Learning',
      color: 'from-cyan-500 to-blue-600',
      description: 'Machine learning algorithms evaluating website visits, email opens, and form submissions to rank high-intent leads for sales reps.',
      bullets: ['Predictive Lead Intent Scoring', 'Automated Engagement Triggers', 'Conversion Propensity Model']
    },
    {
      icon: MessageSquare,
      title: 'Omnichannel Communication Hub',
      badge: 'Unified Inbox',
      color: 'from-purple-500 to-indigo-600',
      description: 'Centralized inbox combining WhatsApp Business API, Email, Live Web Chat, Telephony, and SMS into a single customer conversation history.',
      bullets: ['WhatsApp & Email Unified Inbox', '2-Way SMS & Call Logging', 'Automatic Activity Timeline']
    },
    {
      icon: Mail,
      title: 'Marketing Automation & Email Copilot',
      badge: 'Drip Campaigns',
      color: 'from-emerald-500 to-teal-600',
      description: 'Automated email sequence drip campaigns, dynamic list segmentation, and AI generative email copy tailored to prospect industries.',
      bullets: ['Behavioral Drip Sequences', 'Dynamic Customer Segmentation', 'AI Email Copy Generator']
    },
    {
      icon: FileText,
      title: 'Quote & Contract e-Signature Engine',
      badge: 'Fast Closing',
      color: 'from-pink-500 to-rose-600',
      description: 'Generate PDF quotes in one click, request legal e-signatures, track proposal opens, and trigger automated contract renewal reminders.',
      bullets: ['1-Click PDF Quote Generator', 'Integrated Legal e-Signatures', 'Automated Renewal Alerts']
    },
    {
      icon: BarChart3,
      title: 'Executive BI & Revenue Analytics',
      badge: 'Executive View',
      color: 'from-blue-500 to-cyan-600',
      description: 'Real-time financial telemetry reporting Monthly Recurring Revenue (MRR), Annual Recurring Revenue (ARR), CAC, LTV, and rep leaderboards.',
      bullets: ['Real-Time MRR / ARR Telemetry', 'Sales Rep Performance Matrix', 'Customer Lifetime Value (LTV)']
    }
  ];

  const moduleSpecs = {
    sales: {
      name: 'Sales Cloud & Pipeline Automation',
      desc: 'Empower sales reps to manage 5x more deals with automated task reminders, email templates, and real-time deal probability calculations.',
      specs: ['Custom Deal Stage Pipelines', 'Automated Task Reminders', '1-Click Call & Meeting Scheduler', 'Win/Loss Analytics Engine']
    },
    service: {
      name: 'Customer Service & Desk Support',
      desc: 'Resolve customer tickets 50% faster with automated SLA escalation, knowledge base search, and AI auto-response suggestions.',
      specs: ['Multi-Channel Ticket Queues', 'SLA Breach Escalation Rules', 'Self-Service Knowledge Portal', 'AI Response Suggestion Engine']
    },
    marketing: {
      name: 'Marketing Automation & Lead Nurturing',
      desc: 'Attract, capture, and nurture prospects into sales-ready leads with automated landing page forms and behavioral drip triggers.',
      specs: ['Custom Form Builder & Webhooks', 'Automated Lead Nurture Drips', 'Campaign ROI Attribution', 'Website Visitor Tracking']
    }
  };

  const crmSteps = [
    { step: '01', title: 'Data Migration & Schema Mapping', desc: 'Cleaning and migrating legacy customer databases, contacts, and deal histories with zero data loss.' },
    { step: '02', title: 'Pipeline & Workflow Automation Setup', desc: 'Configuring custom deal stages, lead assignment rules, automated task triggers, and approval queues.' },
    { step: '03', title: 'Omnichannel Communication Integration', desc: 'Connecting corporate Email, WhatsApp Business API, Twilio telephony, and calendar apps.' },
    { step: '04', title: 'Sales Team Training & AI Onboarding', desc: 'Training sales reps and account managers on mobile CRM usage, deal logging, and AI email copilot.' },
    { step: '05', title: 'Executive BI Dashboard & Live Launch', desc: 'Configuring executive revenue reporting dashboards and deploying 24/7 live production support.' }
  ];

  return (
    <div className="min-h-screen bg-gray-950 text-left pt-20 text-gray-100">

      {/* Hero Showcase Section */}
      <section className="py-24 bg-gradient-to-b from-gray-900 via-slate-950 to-gray-950 relative overflow-hidden border-b border-gray-800/80">
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-12 gap-12 items-center">

            {/* Left Copy */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center space-x-2 bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-1.5 mb-6">
                <Users className="w-4 h-4 text-orange-400" />
                <span className="text-orange-400 text-sm font-semibold uppercase tracking-wider">
                  Enterprise CRM Engine v4.0
                </span>
              </div>

              <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
                Custom AI-Powered <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-cyan-400 bg-clip-text text-transparent">Enterprise CRM</span> Platforms
              </h1>

              <p className="text-gray-300 text-base md:text-xl leading-relaxed mb-8">
                Empower your sales, marketing, and support teams with automated pipeline tracking, omnichannel lead scoring, AI email copilot, and real-time revenue analytics.
              </p>

              <div className="flex flex-wrap gap-4">
                <a href="/contact">
                  <Button className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-8 py-6 text-base font-bold rounded-xl shadow-lg hover:shadow-orange-500/30 transition-all duration-300 flex items-center gap-2">
                    Request CRM Demo
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </a>
                <a href="#crm-pillars">
                  <Button variant="outline" className="border-gray-700 text-gray-300 hover:bg-gray-800 px-8 py-6 text-base font-semibold rounded-xl">
                    Explore Capabilities
                  </Button>
                </a>
              </div>
            </div>

            {/* Right Live CRM Telemetry Console */}
            <div className="lg:col-span-5">
              <div className="bg-gray-900/90 border border-gray-800 rounded-3xl p-6 shadow-2xl relative overflow-hidden backdrop-blur-xl">

                {/* Console Header */}
                <div className="flex items-center justify-between pb-4 border-b border-gray-800 mb-6">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded-full bg-orange-400 animate-ping" />
                    <span className="text-sm font-mono text-orange-400 font-bold">CRM PIPELINE: ACTIVE</span>
                  </div>
                  <span className="text-[11px] font-mono text-gray-400">SALES ENGINE v4.2</span>
                </div>

                {/* Telemetry Stats Grid */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-950/80 border border-gray-800 p-4 rounded-xl">
                    <div className="text-sm text-gray-400 mb-1">Quarterly Pipeline</div>
                    <div className="text-2xl font-extrabold text-cyan-400 font-mono">$4.8M</div>
                  </div>
                  <div className="bg-gray-950/80 border border-gray-800 p-4 rounded-xl">
                    <div className="text-sm text-gray-400 mb-1">Lead Win Rate</div>
                    <div className="text-2xl font-extrabold text-orange-400 font-mono">38.6%</div>
                  </div>
                  <div className="bg-gray-950/80 border border-gray-800 p-4 rounded-xl">
                    <div className="text-sm text-gray-400 mb-1">Active Deals</div>
                    <div className="text-2xl font-extrabold text-emerald-400 font-mono">142 DEALS</div>
                  </div>
                  <div className="bg-gray-950/80 border border-gray-800 p-4 rounded-xl">
                    <div className="text-sm text-gray-400 mb-1">AI Lead Scoring</div>
                    <div className="text-2xl font-extrabold text-blue-400 font-mono">ENABLED</div>
                  </div>
                </div>

                {/* Terminal Line */}
                <div className="bg-gray-950 p-3 rounded-xl border border-gray-800 font-mono text-sm text-gray-400 flex items-center gap-2">
                  <Terminal className="w-4 h-4 text-orange-400" />
                  <span>crm_engine.assign_high_intent_lead(#DEAL-8402)</span>
                </div>

              </div>
            </div>

          </div>
        </div>

        {/* Ambient Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-orange-500/10 rounded-full blur-3xl pointer-events-none" />
      </section>

      {/* CRM Performance Metrics Banner */}
      <section className="py-10 bg-gray-900/80 border-y border-gray-800/80">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl md:text-4xl font-extrabold text-cyan-400 mb-1">35%+</div>
              <div className="text-sm text-gray-400 font-semibold uppercase tracking-wider">Higher Deal Close Rate</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-extrabold text-orange-400 mb-1">4.8x</div>
              <div className="text-sm text-gray-400 font-semibold uppercase tracking-wider">Sales Pipeline Velocity</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-extrabold text-blue-400 mb-1">100%</div>
              <div className="text-sm text-gray-400 font-semibold uppercase tracking-wider">Omnichannel Sync</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-extrabold text-emerald-400 mb-1">360°</div>
              <div className="text-sm text-gray-400 font-semibold uppercase tracking-wider">Unified Customer Intelligence</div>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars Grid */}
      <section id="crm-pillars" className="py-24 bg-gray-950">
        <div className="container mx-auto px-6">

          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-block bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-1.5 mb-4">
              <span className="text-orange-400 text-sm font-semibold uppercase tracking-wider">
                Full-Spectrum CRM Features
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
              Enterprise CRM <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-cyan-400 bg-clip-text text-transparent">Pillars</span>
            </h2>
            <p className="text-gray-300 text-base md:text-lg leading-relaxed">
              From AI-powered lead scoring to automated email drips, omnichannel inbox, and executive revenue dashboards.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {crmPillars.map((pillar, idx) => (
              <div
                key={idx}
                className="group bg-gray-900/90 border border-gray-800 hover:border-orange-500/70 rounded-2xl p-8 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-orange-500/10 flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div className={`w-14 h-14 bg-gradient-to-br ${pillar.color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-md`}>
                      <pillar.icon className="w-7 h-7 text-white" />
                    </div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-cyan-400 bg-cyan-500/10 border border-cyan-500/20 px-2.5 py-1 rounded-full">
                      {pillar.badge}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-amber-300 transition-colors">
                    {pillar.title}
                  </h3>

                  <p className="text-gray-300 text-sm leading-relaxed mb-6">
                    {pillar.description}
                  </p>
                </div>

                <div className="pt-4 border-t border-gray-800/80 space-y-2">
                  {pillar.bullets.map((bullet, i) => (
                    <div key={i} className="flex items-center text-sm text-gray-300 font-medium">
                      <CheckCircle2 className="w-3.5 h-3.5 text-orange-400 mr-2 flex-shrink-0" />
                      <span>{bullet}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Interactive CRM Module Selector */}
      <section className="py-20 bg-gradient-to-b from-gray-950 via-slate-900 to-gray-950 border-t border-gray-800">
        <div className="container mx-auto px-6 max-w-5xl">

          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-white mb-4">
              Explore Enterprise CRM Modules
            </h2>
            <p className="text-gray-400 text-sm max-w-xl mx-auto mb-8">
              Modular CRM architecture tailored specifically for your B2B sales cycle, customer support desk, or marketing engine.
            </p>

            <div className="inline-flex p-1 bg-gray-900 border border-gray-800 rounded-xl space-x-2">
              <button
                onClick={() => setActiveModule('sales')}
                className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${activeModule === 'sales'
                  ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                Sales Cloud
              </button>
              <button
                onClick={() => setActiveModule('service')}
                className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${activeModule === 'service'
                  ? 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-md'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                Customer Service Desk
              </button>
              <button
                onClick={() => setActiveModule('marketing')}
                className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${activeModule === 'marketing'
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-md'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                Marketing Automation
              </button>
            </div>
          </div>

          <div className="bg-gray-900/90 border border-gray-800 rounded-3xl p-8 md:p-12 shadow-2xl">
            <h3 className="text-2xl font-bold text-white mb-3">
              {moduleSpecs[activeModule].name}
            </h3>
            <p className="text-gray-300 text-sm leading-relaxed mb-8">
              {moduleSpecs[activeModule].desc}
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              {moduleSpecs[activeModule].specs.map((spec, idx) => (
                <div key={idx} className="bg-gray-950/80 border border-gray-800 rounded-xl p-4 flex items-center space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-orange-400 flex-shrink-0" />
                  <span className="text-sm font-semibold text-gray-200">{spec}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* 5-Step CRM Implementation Roadmap */}
      <section className="py-24 bg-gray-950 border-t border-gray-800">
        <div className="container mx-auto px-6 max-w-5xl">

          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-3">
              5-Step CRM Deployment Roadmap
            </h2>
            <p className="text-gray-400 text-sm max-w-xl mx-auto">
              From data migration to workflow automation configuration and executive BI dashboard launch.
            </p>
          </div>

          <div className="space-y-6">
            {crmSteps.map((proc, idx) => (
              <div key={idx} className="bg-gray-900/80 border border-gray-800 rounded-2xl p-6 flex flex-col md:flex-row items-start md:items-center gap-6 hover:border-orange-500/50 transition-all">
                <span className="text-3xl font-black text-orange-400 font-mono flex-shrink-0">{proc.step}</span>
                <div>
                  <h4 className="text-lg font-bold text-white mb-1">{proc.title}</h4>
                  <p className="text-sm text-gray-400 leading-relaxed">{proc.desc}</p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Full-Width Executive Consultation CTA Section */}
      <section className="py-24 bg-gradient-to-r from-slate-900 via-gray-950 to-slate-900 relative overflow-hidden border-t border-gray-800/80 w-full">
        <div className="container mx-auto px-6 relative z-10 max-w-4xl text-center">
          <div className="bg-gradient-to-r from-slate-900 via-gray-800 to-slate-900 border border-orange-500/30 rounded-3xl p-10 md:p-14 shadow-2xl">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
              Ready to Upgrade Your Enterprise CRM Platform?
            </h2>
            <p className="text-gray-300 text-base mb-8 max-w-2xl mx-auto">
              Schedule a technical session with our CRM architects to design custom sales pipelines, AI lead scoring, and omnichannel automation.
            </p>
            <a href="/contact">
              <Button className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-8 py-6 text-base font-bold rounded-xl shadow-lg">
                Schedule Executive CRM Demo
              </Button>
            </a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default CRMSolution;